import React from 'react';
import { Button } from '../ui/button';
import { Clock, Search, X, Filter } from 'lucide-react';

interface SearchResultsSummaryProps {
  totalCount: number;
  searchDurationMs?: number;
  currentPage: number;
  pageSize: number;
  hasActiveFilters: boolean;
  activeFiltersCount?: number;
  searchTerm?: string;
  onClearSearch?: () => void;
  onClearFilters?: () => void;
  className?: string;
}

export const SearchResultsSummary: React.FC<SearchResultsSummaryProps> = ({
  totalCount,
  searchDurationMs,
  currentPage,
  pageSize,
  hasActiveFilters,
  activeFiltersCount = 0,
  searchTerm,
  onClearSearch,
  onClearFilters,
  className
}) => {
  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, totalCount);

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  return (
    <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${className}`}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        {/* Results count and range */}
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Search className="w-4 h-4" />
          <span className="font-medium">
            {totalCount.toLocaleString()} result{totalCount !== 1 ? 's' : ''}
          </span>
          {totalCount > 0 && (
            <span className="text-gray-500">
              (showing {startIndex.toLocaleString()} - {endIndex.toLocaleString()})
            </span>
          )}
        </div>

        {/* Search duration */}
        {searchDurationMs !== undefined && (
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <Clock className="w-3 h-3" />
            <span>{formatDuration(searchDurationMs)}</span>
          </div>
        )}
      </div>

      {/* Active filters and search term indicators */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Current search term */}
        {searchTerm && (
          <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 border border-blue-200 rounded-md text-sm">
            <Search className="w-3 h-3 text-blue-600" />
            <span className="text-blue-700 max-w-32 truncate" title={searchTerm}>
              "{searchTerm}"
            </span>
            {onClearSearch && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onClearSearch}
                className="h-4 w-4 p-0 ml-1 hover:bg-blue-100"
                aria-label="Clear search term"
              >
                <X className="w-3 h-3" />
              </Button>
            )}
          </div>
        )}

        {/* Active filters indicator */}
        {hasActiveFilters && (
          <div className="flex items-center gap-1 px-2 py-1 bg-green-50 border border-green-200 rounded-md text-sm">
            <Filter className="w-3 h-3 text-green-600" />
            <span className="text-green-700">
              {activeFiltersCount} filter{activeFiltersCount !== 1 ? 's' : ''}
            </span>
            {onClearFilters && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onClearFilters}
                className="h-4 w-4 p-0 ml-1 hover:bg-green-100"
                aria-label="Clear all filters"
              >
                <X className="w-3 h-3" />
              </Button>
            )}
          </div>
        )}

        {/* No results message */}
        {totalCount === 0 && (searchTerm || hasActiveFilters) && (
          <div className="text-sm text-gray-500 italic">
            Try adjusting your search or filters
          </div>
        )}
      </div>
    </div>
  );
};